import { useEffect, useState } from 'react';
import { FiEdit2, FiInfo } from 'react-icons/fi';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { updateSample } from '../../api/data';
import { ConversionParameters, UpdateSamplePayload } from '../../@types/samples/types';
import { Accordion } from '../accordion';
import { Button } from '../button';
import { TextField } from '../text-field';
import { Select } from '../select';
import styles from '../sample-information/SampleInformation.module.css';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const calculateIftCos = (ift: number | null, angle: number | null): number | null => {
  if (ift === null || !Number.isFinite(ift)) return null;
  const radians = ((angle ?? 0) * Math.PI) / 180;
  return Math.abs(ift * Math.cos(radians));
};

const roundConversion = (value: number | null): number | null =>
  value === null ? null : Math.round(value * 10000) / 10000;

type IconProps = { size?: number; style?: React.CSSProperties };
const PencilIcon = FiEdit2 as React.FC<IconProps>;
const InfoIcon = FiInfo as React.FC<IconProps>;

const LAB_SYSTEM_OPTIONS = [
  { value: 'Air-Brine', label: 'Air-Brine' },
  { value: 'Air-Oil',   label: 'Air-Oil' },
  { value: 'Oil-Brine', label: 'Oil-Brine' },
  { value: 'Air-Hg',   label: 'Air-Hg' },
];

const RESERVOIR_SYSTEM_OPTIONS = [
  { value: 'Air-Brine', label: 'Air-Brine' },
  { value: 'Oil-Brine', label: 'Oil-Brine' },
];

const SYSTEM_KEYS = ['Lab System', 'Reservoir System'];
const PC_VARIABLE_KEYS = ['Salinity', 'PhiRes/PhiLab Factor', 'Qv', 'Water Gradient', 'Oil Gradient'];

const FIELD_UNITS: Record<string, string> = {
  Salinity: 'Kppm NaCl',
  Qv: 'meq/ml',
  'Water Gradient': 'psi/ft',
  'Oil Gradient': 'psi/ft',
};

const FIELD_HINTS: Record<string, string> = {
  Salinity: 'If not provided, default 140 Kppm NaCl is used',
  'PhiRes/PhiLab Factor': 'If not provided, default 1 is used',
  Qv: 'If not provided, default 0 meq/ml is used',
  'Water Gradient': 'Default: 0.486 psi/ft',
  'Oil Gradient': 'Default: 0.373 psi/ft',
};

export const PcVariables = () => {
  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const sampleData = selectedInfo?.sample;

  const [systemEdit, setSystemEdit] = useState(false);
  const [pcEdit, setPcEdit] = useState(false);
  const [savingSection, setSavingSection] = useState<'systems' | 'pc' | null>(null);
  const [editData, setEditData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (sampleData) {
      setEditData({
        'Lab System':           sampleData.lab_system,
        'Reservoir System':     sampleData.reservoir_system,
        Salinity:               sampleData.input_sample_values.salinity,
        'PhiRes/PhiLab Factor': sampleData.input_sample_values.phi_res,
        Qv:                     sampleData.input_sample_values.qv,
        'Water Gradient':       sampleData.water_grad,
        'Oil Gradient':         sampleData.oil_grad,
        laboratory_ift:         sampleData.conversion_parameters?.laboratory_ift,
        laboratory_angle:       sampleData.conversion_parameters?.laboratory_angle,
        reservoir_ift:          sampleData.conversion_parameters?.reservoir_ift,
        reservoir_angle:        sampleData.conversion_parameters?.reservoir_angle,
      });
    }
  }, [sampleData]);

  if (!sampleData) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleConversionChange = (key: keyof Pick<ConversionParameters, 'laboratory_ift' | 'laboratory_angle' | 'reservoir_ift' | 'reservoir_angle'>, value: string) => {
    setEditData(prev => ({ ...prev, [key]: value }));
  };

  const conversionPayload = (): ConversionParameters => {
    const laboratoryIft = toNumberOrNull(editData.laboratory_ift);
    const laboratoryAngle = toNumberOrNull(editData.laboratory_angle);
    const reservoirIft = toNumberOrNull(editData.reservoir_ift);
    const reservoirAngle = toNumberOrNull(editData.reservoir_angle);

    return {
      laboratory_ift: laboratoryIft,
      laboratory_angle: laboratoryAngle,
      ift_cos_lab: roundConversion(calculateIftCos(laboratoryIft, laboratoryAngle)),
      reservoir_ift: reservoirIft,
      reservoir_angle: reservoirAngle,
      ift_cos_res: roundConversion(calculateIftCos(reservoirIft, reservoirAngle)),
      oil_density: sampleData.conversion_parameters?.oil_density ?? null,
    };
  };

  const saveSystems = async () => {
    try {
      setSavingSection('systems');
      if (systemEdit) {
        const payload: UpdateSamplePayload = {
          sample_uid:       sampleData.uid,
          lab_system:       editData['Lab System'],
          reservoir_system: editData['Reservoir System'],
          conversion_parameters: conversionPayload(),
        };
        const response = await updateSample(payload);
        updateData({ wellSamples: data.wellSamples.map(s => s.uid === response.uid ? response : s) });
        setSelectedInfo({ sample: response });
      }
    } catch (error) {
      console.error('System variables update error ===>', error);
    } finally {
      setSystemEdit(e => !e);
      setSavingSection(null);
    }
  };

  const savePcVariables = async () => {
    try {
      setSavingSection('pc');
      if (pcEdit) {
        const payload: UpdateSamplePayload = {
          sample_uid: sampleData.uid,
          input_sample_values: {
            salinity: toNumberOrNull(editData.Salinity),
            phi_res:  toNumberOrNull(editData['PhiRes/PhiLab Factor']),
            qv:       toNumberOrNull(editData.Qv),
          },
          water_grad: toNumberOrNull(editData['Water Gradient']),
          oil_grad:   toNumberOrNull(editData['Oil Gradient']),
        };
        const response = await updateSample(payload);
        updateData({ wellSamples: data.wellSamples.map(s => s.uid === response.uid ? response : s) });
        setSelectedInfo({ sample: response });
      }
    } catch (error) {
      console.error('Pc variables update error ===>', error);
    } finally {
      setPcEdit(e => !e);
      setSavingSection(null);
    }
  };

  const systemHeader = (
    <>
      <span className={styles.titleRow}>
        Fluid Systems & Conversion Parameters
        {systemEdit && <span className={styles.editingChip}>Editing</span>}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {systemEdit && (
          <Button variant="secondary" onClick={() => setSystemEdit(false)}>
            Cancel
          </Button>
        )}
        <Button
          variant={systemEdit ? 'primary' : 'secondary'}
          startIcon={!systemEdit ? <PencilIcon size={12} /> : undefined}
          loading={savingSection === 'systems'}
          onClick={saveSystems}
        >
          {systemEdit ? 'Save' : 'Edit'}
        </Button>
      </div>
    </>
  );

  const pcHeader = (
    <>
      <span className={styles.titleRow}>
        Pc Variables
        {pcEdit && <span className={styles.editingChip}>Editing</span>}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {pcEdit && (
          <Button variant="secondary" onClick={() => setPcEdit(false)}>
            Cancel
          </Button>
        )}
        <Button
          variant={pcEdit ? 'primary' : 'secondary'}
          startIcon={!pcEdit ? <PencilIcon size={12} /> : undefined}
          loading={savingSection === 'pc'}
          onClick={savePcVariables}
        >
          {pcEdit ? 'Save' : 'Edit'}
        </Button>
      </div>
    </>
  );

  const conversionParameters = sampleData.conversion_parameters;
  const activeLabSystem = editData['Lab System'] || sampleData.lab_system;
  const activeReservoirSystem = editData['Reservoir System'] || sampleData.reservoir_system;

  const conversionReference = {
    'Air-Brine': {
      laboratory_ift: 72,
      laboratory_angle: 0,
      ift_cos_lab: 72,
      reservoir_ift: 50,
      reservoir_angle: 0,
      ift_cos_res: 50,
      reservoirEnabled: true,
    },
    'Air-Oil': {
      laboratory_ift: 24,
      laboratory_angle: 0,
      ift_cos_lab: 24,
      reservoir_ift: null,
      reservoir_angle: null,
      ift_cos_res: null,
      reservoirEnabled: false,
    },
    'Oil-Brine': {
      laboratory_ift: 48,
      laboratory_angle: 0,
      ift_cos_lab: 48,
      reservoir_ift: 35,
      reservoir_angle: 60,
      ift_cos_res: 30,
      reservoirEnabled: true,
    },
    'Air-Hg': {
      laboratory_ift: 480,
      laboratory_angle: 140,
      ift_cos_lab: 368,
      reservoir_ift: null,
      reservoir_angle: null,
      ift_cos_res: null,
      reservoirEnabled: false,
    },
  } as const;

  type SystemName = keyof typeof conversionReference;
  const systems = Object.keys(conversionReference) as SystemName[];

  const handleSystemChange = (key: string, value: string) => {
    const system = value as SystemName;
    const reference = conversionReference[system];

    setEditData(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'Lab System'
        ? {
            laboratory_ift: reference.laboratory_ift,
            laboratory_angle: reference.laboratory_angle,
          }
        : {
            reservoir_ift: reference.reservoir_ift,
            reservoir_angle: reference.reservoir_angle,
          })
    }));
  };

  const formatConversionValue = (value: number | null | undefined) =>
    value === null || value === undefined ? 'N/A' : String(Number.isInteger(value) ? value : Number(value.toFixed(4)));

  const getEditableConversionValue = (field: keyof ConversionParameters) => {
    if (field === 'ift_cos_lab') {
      return roundConversion(calculateIftCos(toNumberOrNull(editData.laboratory_ift), toNumberOrNull(editData.laboratory_angle)));
    }
    if (field === 'ift_cos_res') {
      return roundConversion(calculateIftCos(toNumberOrNull(editData.reservoir_ift), toNumberOrNull(editData.reservoir_angle)));
    }
    return toNumberOrNull(editData[field]);
  };

  const getConversionValue = (system: SystemName, field: keyof Omit<typeof conversionReference[SystemName], 'reservoirEnabled'>) => {
    const referenceValue = conversionReference[system][field];

    if (system === activeLabSystem && ['laboratory_ift', 'laboratory_angle', 'ift_cos_lab'].includes(field)) {
      return getEditableConversionValue(field) ?? conversionParameters?.[field] ?? referenceValue;
    }

    if (system === activeReservoirSystem && ['reservoir_ift', 'reservoir_angle', 'ift_cos_res'].includes(field)) {
      return getEditableConversionValue(field) ?? conversionParameters?.[field] ?? referenceValue;
    }

    return referenceValue;
  };

  const conversionRows: Array<{ key: keyof Omit<typeof conversionReference[SystemName], 'reservoirEnabled'>; label: string; type: 'lab' | 'reservoir' }> = [
    { key: 'laboratory_ift', label: 'Laboratory IFT (dyne/cm)', type: 'lab' },
    { key: 'laboratory_angle', label: 'Laboratory Angle', type: 'lab' },
    { key: 'ift_cos_lab', label: 'IFT * Cos q Lab (dyne/cm)', type: 'lab' },
    { key: 'reservoir_ift', label: 'Reservoir IFT (dyne/cm)', type: 'reservoir' },
    { key: 'reservoir_angle', label: 'Reservoir Angle', type: 'reservoir' },
    { key: 'ift_cos_res', label: 'IFT * Cos q Res (dyne/cm)', type: 'reservoir' },
  ];

  const isEditableConversionCell = (system: SystemName, row: typeof conversionRows[number]) =>
    systemEdit &&
    ((row.type === 'lab' && system === activeLabSystem && ['laboratory_ift', 'laboratory_angle'].includes(row.key)) ||
      (row.type === 'reservoir' && system === activeReservoirSystem && ['reservoir_ift', 'reservoir_angle'].includes(row.key)));

  return (
    <Accordion multiple defaultOpen={['system-conversion-parameters', 'pc-variables']}>
      <Accordion.Item id="system-conversion-parameters" header={systemHeader} className={systemEdit ? styles.editingRing : undefined}>
        <div className={styles.systemSelectorGrid}>
          {SYSTEM_KEYS.map(key => (
            <div key={key} className={styles.row}>
              <span className={styles.cellLabel}>{key}</span>
              <div className={styles.cellValue}>
                {systemEdit ? (
                  <Select
                    size="sm"
                    value={editData[key]}
                    options={key === 'Lab System' ? LAB_SYSTEM_OPTIONS : RESERVOIR_SYSTEM_OPTIONS}
                    onChange={val => handleSystemChange(key, val)}
                    fullWidth
                  />
                ) : (
                  <span>{editData[key] ?? 'N/A'}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.conversionTableWrap}>
          <table className={styles.conversionTable}>
            <thead>
              <tr>
                <th>Conversion Parameters</th>
                {systems.map(system => (
                  <th
                    key={system}
                    className={system === activeLabSystem || system === activeReservoirSystem ? styles.selectedSystemColumn : undefined}
                  >
                    {system}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {conversionRows.map(row => (
                <tr key={row.key}>
                  <th scope="row">{row.label}</th>
                  {systems.map(system => {
                    const isSelectedLab = row.type === 'lab' && system === activeLabSystem;
                    const isSelectedReservoir = row.type === 'reservoir' && system === activeReservoirSystem;
                    const isDisabledReservoir = row.type === 'reservoir' && !conversionReference[system].reservoirEnabled;
                    const editable = isEditableConversionCell(system, row);
                    return (
                      <td
                        key={`${row.key}-${system}`}
                        className={[
                          isSelectedLab || isSelectedReservoir ? styles.selectedSystemCell : '',
                          isDisabledReservoir ? styles.notApplicableCell : ''
                        ].filter(Boolean).join(' ')}
                      >
                        {isDisabledReservoir ? '' : editable ? (
                          <TextField
                            size="sm"
                            name={row.key}
                            value={editData[row.key] ?? ''}
                            onChange={event => handleConversionChange(row.key as keyof Pick<ConversionParameters, 'laboratory_ift' | 'laboratory_angle' | 'reservoir_ift' | 'reservoir_angle'>, event.target.value)}
                            fullWidth
                          />
                        ) : formatConversionValue(getConversionValue(system, row.key))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.cellHint} style={{ marginTop: 8 }}>
          Highlighted cells are the selected lab and reservoir systems. The highlighted IFT * Cos q values are used in the Pres correction.
        </p>
      </Accordion.Item>

      <Accordion.Item id="pc-variables" header={pcHeader} className={pcEdit ? styles.editingRing : undefined}>
        <div className={styles.grid}>
          {PC_VARIABLE_KEYS.map(key => {
            const value = editData[key];
            const unit = FIELD_UNITS[key];
            const hint = FIELD_HINTS[key];
            const isMissing = value === null || value === undefined || value === '';

            return (
              <div key={key} className={styles.row}>
                <span className={styles.cellLabel}>
                  {key}
                  {unit && <span className={styles.cellUnit}> ({unit})</span>}
                </span>
                <div className={styles.cellValue}>
                  {pcEdit ? (
                    <TextField size="sm" name={key} value={value ?? ''} onChange={handleInputChange} fullWidth />
                  ) : (
                    <span>{isMissing ? 'N/A' : String(value)}</span>
                  )}
                  {hint && !pcEdit && (
                    <span className={styles.cellHint}>
                      <InfoIcon size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                      {hint}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Accordion.Item>
    </Accordion>
  );
};

export default PcVariables;
